import { useState } from 'react';
import axios from 'axios';
import { Upload, UserPlus, FileText, X } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function ContactImport({ campaignId, onImportComplete }) {
  const [showModal, setShowModal] = useState(false);
  const [importType, setImportType] = useState('manual'); // 'manual' or 'csv'
  const [manualContact, setManualContact] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    company_name: '',
    title: ''
  });
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/campaigns/${campaignId}/contacts`,
        manualContact,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setManualContact({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        company_name: '',
        title: ''
      });
      onImportComplete?.();
      setShowModal(false);
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', csvFile);

      await axios.post(
        `${API_BASE}/dialer/campaigns/${campaignId}/contacts/import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setCsvFile(null);
      onImportComplete?.();
      setShowModal(false);
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Failed to import CSV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
      >
        <UserPlus className="w-4 h-4" />
        Add Contacts
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Add Contacts</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setImportType('manual')}
                className={`px-4 py-2 rounded ${
                  importType === 'manual'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                <UserPlus className="w-4 h-4 inline mr-2" />
                Manual Entry
              </button>
              <button
                onClick={() => setImportType('csv')}
                className={`px-4 py-2 rounded ${
                  importType === 'csv'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                CSV Import
              </button>
            </div>

            {importType === 'manual' ? (
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={manualContact.first_name}
                      onChange={(e) =>
                        setManualContact({ ...manualContact, first_name: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={manualContact.last_name}
                      onChange={(e) =>
                        setManualContact({ ...manualContact, last_name: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={manualContact.phone}
                    onChange={(e) =>
                      setManualContact({ ...manualContact, phone: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                    placeholder="+1234567890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={manualContact.email}
                    onChange={(e) =>
                      setManualContact({ ...manualContact, email: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Company</label>
                  <input
                    type="text"
                    value={manualContact.company_name}
                    onChange={(e) =>
                      setManualContact({ ...manualContact, company_name: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    value={manualContact.title}
                    onChange={(e) =>
                      setManualContact({ ...manualContact, title: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {loading ? 'Adding...' : 'Add Contact'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCsvUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Upload CSV File
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        {csvFile ? csvFile.name : 'Click to upload CSV file'}
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    CSV should have columns: first_name, last_name, phone, email, company_name, title
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !csvFile}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {loading ? 'Importing...' : 'Import CSV'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}


